import { Link, NavLink } from "react-router-dom";
import { BookOpenText, Orbit, ScrollText, Sparkles } from "lucide-react";

import { LanguageToggle } from "@/components/LanguageToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { navigationLabels, pickText } from "@/lib/content";

export const SiteShell = ({ children, language, onLanguageChange, summary, summaryLoading, summaryError, vipEmail, vipState }) => {
  const navigation = navigationLabels[language];

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="pointer-events-none fixed inset-0 opacity-80">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.08),transparent_30%)]" />
        <div className="absolute right-[-10rem] top-20 h-80 w-80 rounded-full bg-[#3E4C59]/20 blur-3xl" />
        <div className="absolute bottom-0 left-[-6rem] h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      </div>

      <header className="shell-container sticky top-0 z-40 pt-6">
        <div className="glass-card grain-mask flex flex-col gap-6 rounded-[2rem] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-3 lg:max-w-xl">
            <Link className="flex items-center gap-3 text-foreground" data-testid="site-brand-link" to="/">
              <span className="orbital-glow flex h-12 w-12 items-center justify-center rounded-full border border-primary/30 bg-black/40 text-primary">
                <Orbit className="h-5 w-5" />
              </span>
              <div>
                <p className="section-label" data-testid="site-brand-eyebrow">
                  Project Adam_Kadmon_01
                </p>
                <h1 className="text-3xl leading-none text-foreground" data-testid="site-brand-title">
                  Anatomia Occulti
                </h1>
              </div>
            </Link>
            <p className="body-copy max-w-xl" data-testid="site-brand-subtitle">
              {pickText(
                language,
                "A living temple atlas connecting Kabbalah, chakras, tattvas, and symbolic anatomy.",
                "Un atlante del tempio vivente che collega Qabbalah, chakra, tattva e anatomia simbolica.",
              )}
            </p>
          </div>

          <div className="flex flex-col gap-4 lg:items-end">
            <nav className="flex flex-wrap gap-2" data-testid="site-navigation">
              {navigation.map((item) => (
                <NavLink
                  key={item.to}
                  className={({ isActive }) =>
                    `rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-300 ${
                      isActive
                        ? "border-primary/50 bg-primary/10 text-primary"
                        : "border-white/10 text-slate-300 hover:border-primary/30 hover:text-primary"
                    }`
                  }
                  data-testid={`nav-link-${item.id}`}
                  to={item.to}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex flex-wrap items-center gap-3">
              <div className="glass-card flex items-center gap-3 rounded-full px-4 py-3" data-testid="header-summary-pill">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs uppercase tracking-[0.25em] text-slate-300" data-testid="header-summary-text">
                  {summaryLoading
                    ? pickText(language, "Loading temple archive", "Caricamento archivio del tempio")
                    : `${summary?.counts?.entries ?? 0} ${pickText(language, "mapped correspondences", "corrispondenze mappate")}`}
                </span>
              </div>
              <div className="glass-card flex items-center gap-3 rounded-full px-4 py-3" data-testid="header-vip-pill">
                <span className="text-xs uppercase tracking-[0.25em] text-primary" data-testid="header-vip-status-text">
                  {vipState?.access_granted
                    ? pickText(language, "VIP active", "VIP attivo")
                    : pickText(language, "VIP locked", "VIP bloccato")}
                </span>
                {vipEmail ? (
                  <span className="text-xs text-slate-400" data-testid="header-vip-email-text">
                    {vipEmail}
                  </span>
                ) : null}
              </div>
              <LanguageToggle language={language} onChange={onLanguageChange} />
              {vipState?.access_granted && <NotificationBell language={language} vipEmail={vipEmail} />}
            </div>
          </div>
        </div>
      </header>

      {summaryError ? (
        <div className="shell-container pt-4">
          <div className="glass-card rounded-3xl border-red-500/30 px-5 py-4 text-sm text-red-200" data-testid="summary-error-banner">
            {summaryError}
          </div>
        </div>
      ) : null}

      <main className="shell-container relative z-10 pb-20 pt-10">{children}</main>

      <footer className="shell-container pb-8">
        <div className="glass-card flex flex-col gap-5 rounded-[2rem] px-6 py-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <p className="section-label" data-testid="footer-eyebrow">
              {pickText(language, "Method of Reading", "Metodo di Lettura")}
            </p>
            <p className="body-copy max-w-2xl" data-testid="footer-description">
              {pickText(
                language,
                "This archive treats the body as a symbolic operating system. Sensitive material is handled with abstraction and historical care.",
                "Questo archivio tratta il corpo come un sistema operativo simbolico. Il materiale sensibile è trattato con astrazione e attenzione storica.",
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.25em] text-slate-400">
            <div className="flex items-center gap-2" data-testid="footer-pillars-item">
              <ScrollText className="h-4 w-4 text-primary" />
              <span>{summary?.counts?.glossary ?? 0} glossary terms</span>
            </div>
            <div className="flex items-center gap-2" data-testid="footer-archive-item">
              <BookOpenText className="h-4 w-4 text-primary" />
              <span>{summary?.counts?.symbols ?? 0} symbolic entries</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};